import React, { useEffect, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import LinearProgress from '@mui/material/LinearProgress';
import Swal from 'sweetalert2';
import { obtenerHorasVoluntariado, ajustarHorasVoluntariado } from '../../../services/HorasVoluntariadoService';
import JumpByItemFooter from '../customTableFooter/JumpByItemFooter';
import './horasVoluntariado.css';

export const formatearHoras = (seconds = 0) => {
  const minutes = Math.floor(Math.abs(seconds) / 60);
  return `${seconds < 0 ? '-' : ''}${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

const formatoFecha = new Intl.DateTimeFormat('en', {
  timeZone: 'America/Managua', year: 'numeric', month: '2-digit', day: '2-digit'
});
const formatoFechaHora = new Intl.DateTimeFormat('es-NI', {
  timeZone: 'America/Managua', year: 'numeric', month: 'numeric', day: 'numeric',
  hour: 'numeric', minute: 'numeric', second: 'numeric'
});
const slotsHistorial = { footer: JumpByItemFooter };
const opcionesPagina = [];
const identificarEvento = (row) => `${row.tipo}-${row.id}`;

const fechaHoy = () => {
  const parts = formatoFecha.formatToParts(new Date());
  const part = (type) => parts.find((value) => value.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const fechaHora = (value) => value ? formatoFechaHora.format(new Date(value)) : '—';
const tipos = {
  LOGIN: 'Inicio de sesión', REFRESH: 'Renovación', LOGOUT: 'Cierre de sesión',
  EXPIRACION: 'Vencimiento', DESHABILITADO: 'Cuenta deshabilitada', AJUSTE: 'Ajuste'
};

const columnas = [
  { field: 'instante', headerName: 'Fecha y hora', width: 185, renderCell: ({ value }) => fechaHora(value) },
  { field: 'tipo', headerName: 'Evento', width: 185, renderCell: ({ value }) => tipos[value] || value },
  { field: 'expiracion', headerName: 'Válido hasta', width: 185, renderCell: ({ value }) => fechaHora(value) },
  { field: 'sesionId', headerName: 'Sesión', width: 280 },
  { field: 'fecha', headerName: 'Fecha del ajuste', width: 150 },
  { field: 'minutos', headerName: 'Ajuste', width: 130, renderCell: ({ value }) => value == null ? '—' : formatearHoras(value * 60) },
  { field: 'editorNombre', headerName: 'Registrado por', width: 200 },
  { field: 'motivo', headerName: 'Motivo', width: 300 }
].map((column) => ({ ...column, sortable: false, headerClassName: 'tablaVoluntarios-headers' }));

function HorasVoluntariado({ voluntario, onClose, onUpdated }) {
  const [historial, setHistorial] = useState(null);
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [fecha, setFecha] = useState(fechaHoy);
  const [operacion, setOperacion] = useState('sumar');
  const [horas, setHoras] = useState('0');
  const [minutos, setMinutos] = useState('0');
  const [motivo, setMotivo] = useState('');
  const [errorAjuste, setErrorAjuste] = useState('');
  const envio = useRef(null);
  const guardandoRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    obtenerHorasVoluntariado(voluntario.id, page, 20)
      .then(({ data }) => { if (active) setHistorial(data); })
      .catch(() => { if (active) setError('No se pudo cargar el historial de horas.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [voluntario.id, page, revision]);

  const guardarAjuste = async () => {
    if (guardandoRef.current) return;
    const h = Number(horas);
    const m = Number(minutos);
    const totalMinutos = h * 60 + m;
    if (!fecha || fecha > fechaHoy() || !Number.isInteger(h) || !Number.isInteger(m)
      || h < 0 || m < 0 || m > 59 || totalMinutos <= 0 || totalMinutos > 5256000) {
      setErrorAjuste('Indique una fecha no futura y un tiempo válido; los minutos deben estar entre 0 y 59.');
      return;
    }
    const contenido = {
      fecha, minutos: (operacion === 'restar' ? -1 : 1) * totalMinutos, motivo: motivo.trim() || null
    };
    const firma = JSON.stringify(contenido);
    // Preserve the request ID after a failed submission so retrying cannot duplicate it.
    if (envio.current?.firma !== firma) {
      envio.current = { firma, solicitudId: window.crypto.randomUUID() };
    }
    guardandoRef.current = true;
    setGuardando(true);
    setErrorAjuste('');
    try {
      await ajustarHorasVoluntariado(voluntario.id, { ...contenido, solicitudId: envio.current.solicitudId });
      envio.current = null;
      setHoras('0');
      setMinutos('0');
      setMotivo('');
      setPage(0);
      setRevision((value) => value + 1);
      onUpdated();
      await Swal.fire({
        title: 'Éxito!', text: 'Ajuste de horas registrado con éxito.', icon: 'success',
        confirmButtonText: 'Ok', confirmButtonColor: '#21807C', customClass: { container: 'swal-overlay' }
      });
    } catch (failure) {
      setErrorAjuste(failure.response?.data?.message || 'No se pudo confirmar el ajuste. Puede reintentar el mismo envío sin duplicarlo.');
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const cerrar = () => { if (!guardandoRef.current) onClose(); };

  return (
    <Dialog open onClose={cerrar} fullWidth maxWidth="lg">
      <DialogTitle>Horas de voluntariado — {voluntario.nombreCompleto}</DialogTitle>
      <DialogContent>
        <p>El tiempo se registra desde el inicio de sesión hasta su cierre o vencimiento.
          Las renovaciones automáticas extienden la sesión por dos horas. Los períodos superpuestos se cuentan una sola vez.</p>
        {historial ? (
          <div className="voluntarios-horas-resumen">
            <p>Automáticas: <strong>{formatearHoras(historial.totales.automaticosSegundos)}</strong></p>
            <p>Ajustes: <strong>{formatearHoras(historial.totales.ajustesSegundos)}</strong></p>
            <p>Total: <strong>{formatearHoras(historial.totales.totalSegundos)}</strong></p>
            <p>Calculado: {fechaHora(historial.calculadoEn)}</p>
          </div>
        ) : null}
        {error ? <p role="alert">{error}</p> : null}
        <Button color="success" onClick={() => setRevision((value) => value + 1)} disabled={loading || guardando}>Actualizar</Button>
        <div className="tablaVoluntariosContainer voluntarios-horas-tabla">
          <DataGrid
            rows={historial?.content || []} columns={columnas}
            getRowId={identificarEvento}
            paginationMode="server" pagination
            paginationModel={{ page, pageSize: 20 }}
            onPaginationModelChange={(model) => setPage(model.page)}
            rowCount={historial?.totalItems || 0} loading={loading}
            disableRowSelectionOnClick hideFooterSelectedRowCount
            disableColumnFilter disableColumnMenu pageSizeOptions={opcionesPagina}
            slots={slotsHistorial}
            slotProps={{
              footer: {
                total: historial?.totalItems || 0, page, pageSize: 20,
                onJumpPage: setPage, noun: 'registro'
              }
            }}
          />
        </div>
        <h3>Ajustar total</h3>
        <p>Los ajustes conservan los registros automáticos. El motivo es opcional.</p>
        <RadioGroup row value={operacion} onChange={(event) => setOperacion(event.target.value)}>
          <FormControlLabel value="sumar" control={<Radio />} label="Sumar" disabled={guardando} />
          <FormControlLabel value="restar" control={<Radio />} label="Restar" disabled={guardando} />
        </RadioGroup>
        <div className="voluntarios-horas-campos">
          <TextField label="Fecha del ajuste" type="date" value={fecha} onChange={(event) => setFecha(event.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: fechaHoy() }} disabled={guardando} />
          <TextField label="Horas" type="number" value={horas} onChange={(event) => setHoras(event.target.value)}
            inputProps={{ min: 0, step: 1 }} disabled={guardando} />
          <TextField label="Minutos" type="number" value={minutos} onChange={(event) => setMinutos(event.target.value)}
            inputProps={{ min: 0, max: 59, step: 1 }} disabled={guardando} />
        </div>
        <TextField label="Motivo (opcional)" value={motivo} onChange={(event) => setMotivo(event.target.value)}
          inputProps={{ maxLength: 1000 }} fullWidth multiline margin="dense" disabled={guardando} />
        {errorAjuste ? <p role="alert">{errorAjuste}</p> : null}
        {guardando ? <LinearProgress color="success" /> : null}
      </DialogContent>
      <DialogActions>
        <Button color="success" onClick={guardarAjuste} disabled={guardando || loading}>Registrar ajuste</Button>
        <Button color="error" onClick={cerrar} disabled={guardando}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default HorasVoluntariado;
